/**
 * A nota fiscal na demonstração: as mesmas regras da 0176 e 0177, em memória.
 *
 * Não há emissor nem Vault aqui. O certificado é aceito pelo nome do arquivo
 * (`.pfx`/`.p12`) e ganha validade de um ano — a leitura de verdade é da borda,
 * provada no teste do leitor. "Ligar ao emissor" é simulado (São Paulo fica no
 * padrão municipal, o resto no nacional), e a nota emitida sai autorizada na
 * hora, com número de demonstração: nada vai a prefeitura nenhuma.
 */

import { documentoValido } from './notasFiscais';
import type {
    AlvoFiscal, CertificadoFiscal, DadosDoEmitente, EmitenteFiscal, FiltroDeNotas, NotaFiscal, PadraoNfse,
} from './notasFiscais';
import type { PortaDeDados } from './porta';
import type { FichaDoPaciente, Titulo } from './tipos';

type MetodosDaNotaFiscal = Pick<PortaDeDados,
    | 'emitenteFiscal' | 'salvarEmitenteFiscal' | 'enviarCertificadoFiscal' | 'removerCertificadoFiscal'
    | 'notasFiscais' | 'prepararNotaDoTitulo' | 'prepararNotaDaFatura' | 'atualizarRascunhoDaNota'
    | 'descartarRascunhoDaNota' | 'solicitarEmissaoDaNota' | 'ligarEmissorFiscal' | 'cancelarNotaFiscal'
>;

export interface FaturaParaNota {
    id: string;
    clinicaId: string;
    competencia: string;
    vencimento: string;
    valorCentavos: number;
    descricao?: string;
    estornadaEm?: string;
}

export interface DependenciasDaNotaFiscalNaDemo {
    pode: (permissao: string) => boolean;
    /** Perfil de plataforma: a proprietária tem `*`, e nem por isso configura a nota da plataforma. */
    operador: () => boolean;
    clinicaId: () => string | undefined;
    titulo: (id: string) => Titulo | undefined;
    ficha: (contatoId: string) => FichaDoPaciente | undefined;
    fatura: (id: string) => FaturaParaNota | undefined;
    nomeDaClinica: (id: string) => string;
}

interface NotaGuardada extends Omit<NotaFiscal, 'pendencias' | 'valorIssCentavos'> {
    chaveDoEmitente: string;
    contatoId?: string;
}

let emitentes = new Map<string, EmitenteFiscal>();
let notas: NotaGuardada[] = [];
let sequencia = 0;

export function reiniciarNotasFiscaisDaDemonstracao(): void {
    emitentes = new Map();
    notas = [];
    sequencia = 0;
}

function vazio(): EmitenteFiscal {
    return {
        cnpj: null, razaoSocial: null, nomeFantasia: null, inscricaoMunicipal: null, regimeTributario: null, email: null,
        cep: null, logradouro: null, numero: null, complemento: null, bairro: null, municipio: null, codigoMunicipio: null,
        uf: null, itemListaServico: null, codigoTributacaoMunicipio: null, cnae: null, aliquotaIss: null, issRetido: false,
        descricaoPadrao: null, serieRps: '1', proximoNumeroRps: 1, ambiente: 'homologacao', emitirAoReceber: false,
        codigoTributacaoNacional: null, codigoNbs: null, ibsCbsCst: null, ibsCbsClassificacao: null, regimeEspecial: 0,
        provedor: null, emissor: null, certificado: null,
    };
}

function limpo(v: string | undefined): string | null {
    const t = (v ?? '').trim();
    return t ? t : null;
}

function digitos(v: string | undefined): string | null {
    const d = (v ?? '').replace(/\D/g, '');
    return d ? d : null;
}

function iss(n: Pick<NotaGuardada, 'valorServicosCentavos' | 'aliquotaIss'>): number {
    return Math.round(n.valorServicosCentavos * (n.aliquotaIss ?? 0) / 100);
}

function pendencias(n: NotaGuardada, e: EmitenteFiscal, agora: Date): string[] {
    const p: string[] = [];
    if (!e.cnpj) p.push('CNPJ do emitente');
    if (!e.razaoSocial) p.push('Razão social do emitente');
    if (!e.inscricaoMunicipal) p.push('Inscrição municipal do emitente');
    if (!e.regimeTributario) p.push('Regime tributário do emitente');
    if (!e.codigoMunicipio || !e.cep || !e.logradouro || !e.uf) p.push('Endereço do emitente, com o código IBGE do município');
    if (!e.certificado) p.push('Certificado digital A1');
    else if (new Date(e.certificado.validoAte).getTime() <= agora.getTime()) p.push('Certificado digital vencido');
    else if (e.cnpj && e.certificado.cnpj && e.certificado.cnpj !== e.cnpj) p.push('O certificado é de outro CNPJ');
    if (e.emissor?.padrao === 'nacional') {
        if (!n.codigoTributacaoNacional) p.push('Código de tributação nacional do serviço');
        const serie = /^\d{1,5}$/.test(n.rpsSerie ?? e.serieRps) ? Number(n.rpsSerie ?? e.serieRps) : 0;
        if (serie < 1 || serie > 49999) p.push('Série numérica, de 1 a 49999 (padrão nacional)');
    } else if (!n.itemListaServico) {
        p.push('Item da lista de serviços (LC 116)');
    }
    if (n.aliquotaIss === undefined) p.push('Alíquota de ISS');
    if (!n.descricao.trim()) p.push('Descrição do serviço');
    if (!documentoValido(n.tomadorDocumento)) {
        p.push(n.origem === 'clinica' ? 'CPF ou CNPJ válido do paciente' : 'CNPJ válido da clínica tomadora');
    }
    if (!n.tomadorNome) p.push('Nome do tomador');
    if ((n.tomadorDocumento ?? '').length === 14 && !n.tomadorEndereco) p.push('Endereço do tomador (CEP e município)');
    return p;
}

export function notasFiscaisNaDemonstracao(dep: DependenciasDaNotaFiscalNaDemo): MetodosDaNotaFiscal {
    const chave = (alvo: AlvoFiscal) => {
        if (alvo === 'plataforma') {
            if (!dep.operador() || !dep.pode('faturamento.gerenciar')) throw new Error('Só quem gerencia o faturamento configura a nota da plataforma.');
            return 'plataforma';
        }
        if (!dep.pode('financeiro')) throw new Error('Esta ação exige a permissão do financeiro.');
        const id = dep.clinicaId();
        if (!id) throw new Error('Sessão sem clínica: entre novamente.');
        return id;
    };
    const exigirNota = (id: string) => {
        const n = notas.find((x) => x.id === id);
        if (!n) throw new Error('Nota não encontrada.');
        chave(n.origem);
        return n;
    };
    const servicoDo = (n: NotaGuardada) => {
        const e = emitentes.get(n.chaveDoEmitente) ?? vazio();
        n.itemListaServico = e.itemListaServico ?? undefined;
        n.codigoTributacaoMunicipio = e.codigoTributacaoMunicipio ?? undefined;
        n.codigoTributacaoNacional = e.codigoTributacaoNacional ?? undefined;
        n.aliquotaIss = e.aliquotaIss ?? undefined;
        n.issRetido = e.issRetido;
    };
    const tomadorDo = (n: NotaGuardada) => {
        if (n.origem === 'clinica') {
            const f = n.contatoId ? dep.ficha(n.contatoId) : undefined;
            n.tomadorNome = f?.nome ?? n.tomadorNome;
            n.tomadorDocumento = digitos(f?.cpf) ?? undefined;
            n.tomadorEmail = limpo(f?.email) ?? undefined;
            const endereco = [[f?.rua, f?.numero].filter(Boolean).join(', '), f?.bairro, [f?.cidade, f?.estado].filter(Boolean).join('/')]
                .filter(Boolean).join(' — ');
            n.tomadorEndereco = endereco || undefined;
        } else {
            const e = emitentes.get(n.clinicaId);
            n.tomadorNome = e?.razaoSocial ?? dep.nomeDaClinica(n.clinicaId);
            n.tomadorDocumento = e?.cnpj ?? undefined;
            n.tomadorEndereco = e?.cep && e.municipio ? `${e.logradouro ?? ''}, ${e.numero ?? ''} — ${e.municipio}/${e.uf ?? ''}` : undefined;
        }
    };
    const paraTela = (n: NotaGuardada): NotaFiscal => {
        const resto: Partial<NotaGuardada> = { ...n };
        delete resto.chaveDoEmitente;
        delete resto.contatoId;
        return {
            ...(resto as Omit<NotaGuardada, 'chaveDoEmitente' | 'contatoId'>),
            valorIssCentavos: iss(n),
            pendencias: pendencias(n, emitentes.get(n.chaveDoEmitente) ?? vazio(), new Date()),
        };
    };

    return {
        async emitenteFiscal(alvo: AlvoFiscal): Promise<EmitenteFiscal | null> {
            const e = emitentes.get(chave(alvo));
            return e ? structuredClone(e) : null;
        },

        async salvarEmitenteFiscal(alvo: AlvoFiscal, dados: DadosDoEmitente): Promise<EmitenteFiscal> {
            const k = chave(alvo);
            const e = structuredClone(emitentes.get(k) ?? vazio());
            if (dados.cnpj !== undefined) {
                const c = (dados.cnpj ?? '').toUpperCase().replace(/[^0-9A-Z]/g, '');
                if (c && (c.length !== 14 || !documentoValido(c))) throw new Error('CNPJ inválido.');
                if (e.provedor && (c || null) !== e.cnpj) throw new Error('O CNPJ não muda depois de ligado ao emissor: cada CNPJ é uma empresa lá.');
                e.cnpj = c || null;
            }
            if (dados.razaoSocial !== undefined) e.razaoSocial = limpo(dados.razaoSocial);
            if (dados.nomeFantasia !== undefined) e.nomeFantasia = limpo(dados.nomeFantasia);
            if (dados.inscricaoMunicipal !== undefined) e.inscricaoMunicipal = limpo(dados.inscricaoMunicipal?.replace(/[^0-9A-Za-z]/g, ''));
            if (dados.regimeTributario !== undefined) e.regimeTributario = dados.regimeTributario || null;
            if (dados.email !== undefined) {
                const v = limpo(dados.email);
                if (v && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) throw new Error('E-mail inválido.');
                e.email = v;
            }
            if (dados.cep !== undefined) {
                const v = digitos(dados.cep);
                if (v && v.length !== 8) throw new Error('O CEP tem oito dígitos.');
                e.cep = v;
            }
            if (dados.logradouro !== undefined) e.logradouro = limpo(dados.logradouro);
            if (dados.numero !== undefined) e.numero = limpo(dados.numero);
            if (dados.complemento !== undefined) e.complemento = limpo(dados.complemento);
            if (dados.bairro !== undefined) e.bairro = limpo(dados.bairro);
            if (dados.municipio !== undefined) e.municipio = limpo(dados.municipio);
            if (dados.codigoMunicipio !== undefined) {
                const v = digitos(dados.codigoMunicipio);
                if (v && v.length !== 7) throw new Error('O código IBGE do município tem sete dígitos.');
                e.codigoMunicipio = v;
            }
            if (dados.uf !== undefined) {
                const v = limpo(dados.uf)?.toUpperCase() ?? null;
                if (v && !/^[A-Z]{2}$/.test(v)) throw new Error('A UF tem duas letras.');
                e.uf = v;
            }
            if (dados.itemListaServico !== undefined) {
                let v = limpo(dados.itemListaServico);
                /* Quatro dígitos ou mais: o código da própria prefeitura, como veio (0177). */
                if (v && /^\d{3}$/.test(v)) v = `0${v.slice(0, 1)}.${v.slice(1)}`;
                if (v && /^\d\.\d{2}$/.test(v)) v = `0${v}`;
                if (v && !/^\d{2}\.\d{2}$/.test(v) && !/^\d{4,10}$/.test(v)) throw new Error('O item da lista de serviços é como 06.02, ou o código da prefeitura.');
                e.itemListaServico = v;
            }
            if (dados.codigoTributacaoMunicipio !== undefined) e.codigoTributacaoMunicipio = limpo(dados.codigoTributacaoMunicipio);
            if (dados.cnae !== undefined) {
                const v = digitos(dados.cnae);
                if (v && v.length !== 7) throw new Error('O CNAE tem sete dígitos.');
                e.cnae = v;
            }
            if (dados.aliquotaIss !== undefined) {
                const t = (dados.aliquotaIss ?? '').replace(',', '.').trim();
                const n = t ? Number(t) : null;
                if (n !== null && (!Number.isFinite(n) || n < 0 || n > 5)) throw new Error('A alíquota de ISS vai de 0 a 5%.');
                e.aliquotaIss = n;
            }
            if (dados.issRetido !== undefined) e.issRetido = dados.issRetido;
            if (dados.descricaoPadrao !== undefined) e.descricaoPadrao = limpo(dados.descricaoPadrao);
            if (dados.serieRps !== undefined) {
                const v = limpo(dados.serieRps);
                if (!v) throw new Error('Informe a série do RPS.');
                e.serieRps = v;
            }
            if (dados.ambiente !== undefined) e.ambiente = dados.ambiente;
            if (dados.emitirAoReceber !== undefined) e.emitirAoReceber = dados.emitirAoReceber;
            const codigo = (v: string | undefined, tamanho: number, frase: string) => {
                const d = digitos(v);
                if (d && d.length !== tamanho) throw new Error(frase);
                return d;
            };
            if (dados.codigoTributacaoNacional !== undefined) e.codigoTributacaoNacional = codigo(dados.codigoTributacaoNacional, 6, 'O código de tributação nacional tem seis dígitos, como 040101.');
            if (dados.codigoNbs !== undefined) e.codigoNbs = codigo(dados.codigoNbs, 9, 'O código NBS tem nove dígitos.');
            if (dados.ibsCbsCst !== undefined) e.ibsCbsCst = codigo(dados.ibsCbsCst, 3, 'A situação tributária do IBS/CBS tem três dígitos, como 000.');
            if (dados.ibsCbsClassificacao !== undefined) e.ibsCbsClassificacao = codigo(dados.ibsCbsClassificacao, 6, 'A classificação tributária do IBS/CBS tem seis dígitos, como 000001.');
            if (dados.regimeEspecial !== undefined) {
                const r = Number(dados.regimeEspecial || 0);
                if (![0, 1, 2, 3, 4, 5, 6, 9].includes(r)) throw new Error('Regime especial de tributação desconhecido.');
                e.regimeEspecial = r;
            }
            /* O emissor guarda uma cópia da empresa: mudou o que ele usa, a cópia ficou velha. */
            const daEmpresa: (keyof DadosDoEmitente)[] = ['razaoSocial', 'nomeFantasia', 'inscricaoMunicipal', 'regimeTributario', 'email',
                'cep', 'logradouro', 'numero', 'complemento', 'bairro', 'municipio', 'codigoMunicipio', 'uf'];
            if (e.emissor && daEmpresa.some((c) => dados[c] !== undefined)) e.emissor.desatualizado = true;
            emitentes.set(k, e);
            return structuredClone(e);
        },

        async enviarCertificadoFiscal(alvo: AlvoFiscal, arquivo: Blob, senha: string): Promise<CertificadoFiscal> {
            const k = chave(alvo);
            const nome = (arquivo as File).name ?? '';
            if (!/\.(pfx|p12)$/i.test(nome)) throw new Error('Este arquivo não é um certificado .pfx.');
            if (!senha) throw new Error('Informe a senha do certificado.');
            const e = structuredClone(emitentes.get(k) ?? vazio());
            const cnpj = e.cnpj ?? '12345678000195';
            const agora = new Date();
            const certificado: CertificadoFiscal = {
                titular: `${e.razaoSocial ?? 'CLINICA DE DEMONSTRACAO LTDA'}:${cnpj}`,
                cnpj,
                validoDe: agora.toISOString(),
                validoAte: new Date(agora.getTime() + 365 * 86_400_000).toISOString(),
                enviadoEm: agora.toISOString(),
            };
            e.certificado = certificado;
            e.cnpj = cnpj;
            emitentes.set(k, e);
            return { ...certificado };
        },

        async ligarEmissorFiscal(alvo: AlvoFiscal): Promise<{ padrao: PadraoNfse }> {
            const k = chave(alvo);
            const e = emitentes.get(k);
            if (!e) throw new Error('Preencha e salve os dados fiscais antes de ligar ao emissor.');
            const falta = [
                !e.cnpj && 'CNPJ', !e.razaoSocial && 'razão social', !e.regimeTributario && 'regime tributário',
                (!e.codigoMunicipio || !e.logradouro || !e.cep || !e.uf) && 'endereço com o código IBGE do município',
                !e.certificado && 'certificado digital A1',
            ].filter(Boolean);
            if (falta.length) throw new Error(`Falta para ligar ao emissor: ${falta.join(', ')}.`);
            const padrao: PadraoNfse = e.codigoMunicipio === '3550308' ? 'municipal' : 'nacional';
            e.provedor = 'focus';
            e.emissor = { padrao, ligadoEm: new Date().toISOString(), mensagem: null, cancelaPorApi: true, desatualizado: false };
            return { padrao };
        },

        async removerCertificadoFiscal(alvo: AlvoFiscal): Promise<void> {
            const k = chave(alvo);
            const e = emitentes.get(k);
            if (e) e.certificado = null;
        },

        async notasFiscais(filtro: FiltroDeNotas): Promise<NotaFiscal[]> {
            const clinica = dep.clinicaId();
            return notas
                .filter((n) => n.origem === filtro.origem
                    && (filtro.origem === 'plataforma' && !filtro.tituloId ? true : n.clinicaId === clinica)
                    && (!filtro.tituloId || n.tituloId === filtro.tituloId)
                    && (!filtro.faturaId || n.faturaId === filtro.faturaId))
                .sort((a, b) => b.criadoEm.localeCompare(a.criadoEm))
                .map(paraTela);
        },

        async prepararNotaDoTitulo(tituloId: string, valorCentavos?: number, descricao?: string): Promise<{ id: string }> {
            const k = chave('clinica');
            const t = dep.titulo(tituloId);
            if (!t) throw new Error('Título não encontrado.');
            if (t.tipo !== 'receber' || !t.contatoId) throw new Error('Só título a receber de um paciente tem nota fiscal.');
            if (t.situacao === 'cancelado') throw new Error('Título cancelado não tem nota fiscal.');
            const e = emitentes.get(k);
            if (!e) throw new Error('Configure os dados fiscais da clínica antes (Financeiro › Notas fiscais).');
            const ja = notas.filter((n) => n.tituloId === t.id && n.situacao !== 'cancelada' && n.situacao !== 'rejeitada')
                .reduce((s, n) => s + n.valorServicosCentavos, 0);
            const valor = valorCentavos ?? t.valorCentavos - ja;
            if (valor <= 0) throw new Error('Este título já tem nota do valor inteiro.');
            if (ja + valor > t.valorCentavos) throw new Error('A nota passa do valor do título.');
            sequencia += 1;
            const nota: NotaGuardada = {
                id: `nf-${sequencia}`, origem: 'clinica', clinicaId: k, chaveDoEmitente: k, tituloId: t.id, tituloDescricao: t.descricao,
                contatoId: t.contatoId, situacao: 'rascunho',
                descricao: limpo(descricao) ?? [e.descricaoPadrao, t.descricao].filter(Boolean).join(' — '),
                issRetido: false, valorServicosCentavos: valor, competencia: t.competencia, criadoEm: new Date().toISOString(),
            };
            servicoDo(nota);
            tomadorDo(nota);
            notas.push(nota);
            return { id: nota.id };
        },

        async prepararNotaDaFatura(faturaId: string): Promise<{ id: string }> {
            chave('plataforma');
            const f = dep.fatura(faturaId);
            if (!f) throw new Error('Fatura não encontrada.');
            if (f.estornadaEm) throw new Error('Fatura estornada não tem nota.');
            const e = emitentes.get('plataforma');
            if (!e) throw new Error('Configure os dados fiscais da plataforma antes.');
            if (notas.some((n) => n.faturaId === f.id && n.situacao !== 'cancelada' && n.situacao !== 'rejeitada')) {
                throw new Error('Esta fatura já tem nota.');
            }
            sequencia += 1;
            const nota: NotaGuardada = {
                id: `nf-${sequencia}`, origem: 'plataforma', clinicaId: f.clinicaId, clinicaNome: dep.nomeDaClinica(f.clinicaId),
                chaveDoEmitente: 'plataforma', faturaId: f.id, faturaCompetencia: f.competencia, situacao: 'rascunho',
                descricao: `${e.descricaoPadrao ?? 'Licença de uso do sistema Fórmula MED'}${f.descricao ? ` — ${f.descricao}` : ''}`,
                issRetido: false, valorServicosCentavos: f.valorCentavos, competencia: `${f.competencia}-01`, criadoEm: new Date().toISOString(),
            };
            servicoDo(nota);
            tomadorDo(nota);
            notas.push(nota);
            return { id: nota.id };
        },

        async atualizarRascunhoDaNota(id: string, mudanca: { descricao?: string; valorCentavos?: number }): Promise<void> {
            const n = exigirNota(id);
            if (n.situacao !== 'rascunho' && n.situacao !== 'rejeitada') throw new Error('Só rascunho ou nota rejeitada se edita.');
            if (mudanca.valorCentavos !== undefined) {
                if (mudanca.valorCentavos <= 0) throw new Error('Informe um valor maior que zero.');
                const t = n.tituloId ? dep.titulo(n.tituloId) : undefined;
                const ja = notas.filter((x) => x.tituloId && x.tituloId === n.tituloId && x.id !== n.id && x.situacao !== 'cancelada')
                    .reduce((s, x) => s + x.valorServicosCentavos, 0);
                if (t && ja + mudanca.valorCentavos > t.valorCentavos) throw new Error('A nota passa do valor do título.');
                n.valorServicosCentavos = mudanca.valorCentavos;
            }
            if (limpo(mudanca.descricao)) n.descricao = (mudanca.descricao ?? '').trim();
            servicoDo(n);
            tomadorDo(n);
        },

        async descartarRascunhoDaNota(id: string): Promise<void> {
            const n = exigirNota(id);
            if (n.situacao !== 'rascunho' && n.situacao !== 'rejeitada') throw new Error('Só rascunho ou nota rejeitada se descarta. Nota autorizada se cancela.');
            notas = notas.filter((x) => x.id !== id);
        },

        async solicitarEmissaoDaNota(id: string): Promise<void> {
            const n = exigirNota(id);
            if (n.situacao !== 'rascunho' && n.situacao !== 'rejeitada') throw new Error('Esta nota já foi pedida.');
            const e = emitentes.get(n.chaveDoEmitente) ?? vazio();
            const falta = pendencias(n, e, new Date());
            if (falta.length) throw new Error(`Falta: ${falta.join('; ')}.`);
            if (!e.provedor) throw new Error('A empresa ainda não está ligada ao emissor. Em Dados fiscais, use "Ligar ao emissor".');
            n.rpsSerie = n.rpsSerie ?? e.serieRps;
            if (n.rpsNumero === undefined) {
                n.rpsNumero = e.proximoNumeroRps;
                e.proximoNumeroRps += 1;
            }
            /* Na demonstração a prefeitura responde na hora. */
            n.situacao = 'autorizada';
            n.numero = String(1000 + (n.rpsNumero ?? 0));
            n.codigoVerificacao = `DEMO${n.rpsNumero ?? 0}`;
            n.autorizadaEm = new Date().toISOString();
            delete n.mensagem;
        },

        async cancelarNotaFiscal(id: string, motivo: string): Promise<void> {
            const n = exigirNota(id);
            if (n.situacao !== 'autorizada') throw new Error('Só nota autorizada se cancela.');
            const m = motivo.trim();
            if (m.length < 15 || m.length > 255) throw new Error('Escreva o motivo do cancelamento, de 15 a 255 caracteres.');
            n.situacao = 'cancelada';
            n.cancelamentoMotivo = m;
        },
    };
}
